import {
  CollisionDetection,
  rectIntersection,
  type DropAnimation,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "../ui/card";
import { Dumbbell, Plus } from "lucide-react";
import { Tables } from "@/lib/supabase/supabaseTypes";
import { ExerciseCollection, Workout } from "./workoutsColumns";
import { createWorkoutExercise } from "./workoutEditorUtils";
import {
  TRAILING_LOOSE_SECTION_ID,
  createDefaultSection,
  ensureTrailingLooseSection,
  ensureWorkoutSections,
  firstGroupIndexForSection,
  groupsForSection,
  insertIndexForSection,
  isLooseSection,
} from "@/lib/utils/workoutSections";

export type ExerciseDragData = {
  type: "exercise";
  exerciseId: string;
  exerciseName: string;
  sourceGroupIndex: number;
  sourceExerciseIndex: number;
  sourceSectionId?: string;
};

export type GroupDragData = {
  type: "group";
  groupId: string;
  exerciseCount: number;
  sourceGroupIndex: number;
  sourceSectionId?: string;
};

export type SectionDragData = {
  type: "section";
  sectionId: string;
  sectionName: string;
  sourceSectionIndex: number;
  groupCount: number;
};

export type LibraryExerciseDragData = {
  type: "library-exercise";
  exerciseId: string;
  exerciseName: string;
  exercise: Tables<"exercises">;
};

export type WorkoutDragData =
  | ExerciseDragData
  | GroupDragData
  | SectionDragData
  | LibraryExerciseDragData;

export type GroupInsertDropZoneData = {
  type: "group-insert";
  index: number;
  sectionId?: string;
};

export type SupersetInsertDropZoneData = {
  type: "superset-insert";
  groupIndex: number;
  position: number;
  sectionId?: string;
};

export type SectionInsertDropZoneData = {
  type: "section-insert";
  sectionIndex: number;
};

export type SectionAppendDropZoneData = {
  type: "section-append";
  sectionId: string;
};

export type WorkoutDropZoneData =
  | GroupInsertDropZoneData
  | SupersetInsertDropZoneData
  | SectionInsertDropZoneData
  | SectionAppendDropZoneData;

export type RecentDropState =
  | {
      type: "exercise";
      exerciseId: string;
    }
  | {
      type: "group";
      groupId: string;
    }
  | {
      type: "section";
      sectionId: string;
    };

export const DROP_ANIMATION_DURATION_MS = 400;

let libraryDropTargetId: string | null = null;

export const setLibraryDropTargetId = (exerciseId: string | null) => {
  libraryDropTargetId = exerciseId;
};

const libraryDropTargetElement = () => {
  if (!libraryDropTargetId) return null;
  const selector = `[data-workout-exercise-id="${globalThis.CSS.escape(libraryDropTargetId)}"]`;
  const nodes = document.querySelectorAll(selector);
  for (const node of nodes) {
    if (!(node instanceof HTMLElement)) continue;
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return node;
  }
  return null;
};

const destinationRect = (node: HTMLElement) => {
  const rect = node.getBoundingClientRect();
  const inView =
    rect.bottom > 0 &&
    rect.top < window.innerHeight &&
    rect.right > 0 &&
    rect.left < window.innerWidth;
  if (inView) return rect;
  node.scrollIntoView({ block: "nearest", inline: "nearest" });
  return node.getBoundingClientRect();
};

const isLibraryExerciseDrag = (data: unknown) =>
  typeof data === "object" &&
  data !== null &&
  "current" in data &&
  (data as { current?: { type?: string } }).current?.type === "library-exercise";

const dropTransformToRect = (
  initial: { x: number; y: number; scaleX: number; scaleY: number },
  overlayRect: { left: number; top: number; width: number; height: number },
  destination: DOMRect,
) => {
  const overlayCenterX = overlayRect.left + overlayRect.width / 2;
  const overlayCenterY = overlayRect.top + overlayRect.height / 2;
  const destinationCenterX = destination.left + destination.width / 2;
  const destinationCenterY = destination.top + destination.height / 2;

  return {
    x: initial.x - (overlayCenterX - destinationCenterX),
    y: initial.y - (overlayCenterY - destinationCenterY),
    scaleX: initial.scaleX,
    scaleY: initial.scaleY,
  };
};

export const WORKOUT_DROP_ANIMATION: DropAnimation = {
  duration: DROP_ANIMATION_DURATION_MS,
  easing: "cubic-bezier(0.22, 1, 0.36, 1)",
  keyframes: ({ transform, active, dragOverlay }) => {
    const destination = isLibraryExerciseDrag(active.data)
      ? libraryDropTargetElement()
      : null;
    const final = destination
      ? dropTransformToRect(
          transform.initial,
          dragOverlay.rect,
          destinationRect(destination),
        )
      : transform.final;

    return [
      {
        offset: 0,
        opacity: 1,
        transform: CSS.Transform.toString(transform.initial),
      },
      {
        offset: 0.8,
        opacity: 1,
        transform: CSS.Transform.toString(final),
      },
      {
        offset: 1,
        opacity: 0,
        transform: CSS.Transform.toString(final),
      },
    ];
  },
  sideEffects: ({ active }) => {
    const capturedTargetId = libraryDropTargetId;
    const destination = isLibraryExerciseDrag(active.data)
      ? libraryDropTargetElement()
      : null;
    const node = destination ?? active.node;
    node.style.opacity = "0";

    const fadeInAnimation = node.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 0, offset: 0.8 },
        { opacity: 1, offset: 1 },
      ],
      {
        duration: DROP_ANIMATION_DURATION_MS,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        fill: "both",
      },
    );

    return () => {
      fadeInAnimation.cancel();
      node.style.removeProperty("opacity");
      if (libraryDropTargetId === capturedTargetId) {
        libraryDropTargetId = null;
      }
    };
  },
};

const cloneExerciseCollection = (exerciseCollection: ExerciseCollection[]) =>
  exerciseCollection.map((group) => [...group]);

const normalizeExerciseCollection = (
  exerciseCollection: ExerciseCollection[],
) =>
  exerciseCollection
    .filter((group) => group.length > 0)
    .map((group, groupIndex) =>
      group.map((exercise, exerciseIndex) => ({
        ...exercise,
        ex_order: groupIndex,
        sub_order: exerciseIndex,
      })),
    );

const stampGroupSection = (
  group: ExerciseCollection,
  sectionId?: string,
): ExerciseCollection =>
  sectionId
    ? group.map((exercise) => ({ ...exercise, section_id: sectionId }))
    : group;

export const moveGroupToIndex = (
  exerciseCollection: ExerciseCollection[],
  dragData: GroupDragData,
  dropZone: GroupInsertDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const [group] = groups.splice(dragData.sourceGroupIndex, 1);
  if (!group) {
    return null;
  }

  const adjustedIndex =
    dragData.sourceGroupIndex < dropZone.index
      ? dropZone.index - 1
      : dropZone.index;

  if (adjustedIndex === dragData.sourceGroupIndex) {
    const sourceSectionId =
      dragData.sourceSectionId ?? group[0]?.section_id ?? undefined;
    if (!dropZone.sectionId || dropZone.sectionId === sourceSectionId) {
      return null;
    }
  }

  groups.splice(adjustedIndex, 0, stampGroupSection(group, dropZone.sectionId));

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "group" as const,
      groupId: dragData.groupId,
    },
  };
};

export const moveExerciseToGroupIndex = (
  exerciseCollection: ExerciseCollection[],
  dragData: ExerciseDragData,
  dropZone: GroupInsertDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const sourceGroupWasSingle = groups[dragData.sourceGroupIndex]?.length === 1;

  if (
    sourceGroupWasSingle &&
    (dropZone.index === dragData.sourceGroupIndex ||
      dropZone.index === dragData.sourceGroupIndex + 1)
  ) {
    const sourceSectionId =
      dragData.sourceSectionId ??
      groups[dragData.sourceGroupIndex]?.[0]?.section_id ??
      undefined;
    if (!dropZone.sectionId || dropZone.sectionId === sourceSectionId) {
      return null;
    }
  }

  const [exercise] = groups[dragData.sourceGroupIndex].splice(
    dragData.sourceExerciseIndex,
    1,
  );

  if (!exercise) {
    return null;
  }

  if (groups[dragData.sourceGroupIndex].length === 0) {
    groups.splice(dragData.sourceGroupIndex, 1);
  }

  const adjustedIndex =
    sourceGroupWasSingle && dragData.sourceGroupIndex < dropZone.index
      ? dropZone.index - 1
      : dropZone.index;

  groups.splice(adjustedIndex, 0, stampGroupSection([exercise], dropZone.sectionId));

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "exercise" as const,
      exerciseId: dragData.exerciseId,
    },
  };
};

export const moveExerciseToSupersetPosition = (
  exerciseCollection: ExerciseCollection[],
  dragData: ExerciseDragData,
  dropZone: SupersetInsertDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const sourceGroupWasSingle = groups[dragData.sourceGroupIndex]?.length === 1;
  const sourceGroupIndex = dragData.sourceGroupIndex;
  const targetGroupIndex =
    sourceGroupWasSingle && sourceGroupIndex < dropZone.groupIndex
      ? dropZone.groupIndex - 1
      : dropZone.groupIndex;

  const [exercise] = groups[sourceGroupIndex].splice(
    dragData.sourceExerciseIndex,
    1,
  );

  if (!exercise) {
    return null;
  }

  if (groups[sourceGroupIndex].length === 0) {
    groups.splice(sourceGroupIndex, 1);
  }

  const targetGroup = groups[targetGroupIndex];
  if (!targetGroup) {
    return null;
  }

  const adjustedPosition =
    !sourceGroupWasSingle &&
    sourceGroupIndex === dropZone.groupIndex &&
    dragData.sourceExerciseIndex < dropZone.position
      ? dropZone.position - 1
      : dropZone.position;

  if (
    !sourceGroupWasSingle &&
    sourceGroupIndex === dropZone.groupIndex &&
    adjustedPosition === dragData.sourceExerciseIndex
  ) {
    return null;
  }

  const stampedExercise = dropZone.sectionId
    ? { ...exercise, section_id: dropZone.sectionId }
    : exercise;

  targetGroup.splice(adjustedPosition, 0, stampedExercise);

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "exercise" as const,
      exerciseId: dragData.exerciseId,
    },
  };
};

export const insertLibraryExerciseAtDropZone = (
  exerciseCollection: ExerciseCollection[],
  dragData: LibraryExerciseDragData,
  dropZone: GroupInsertDropZoneData | SupersetInsertDropZoneData,
) => {
  const groups = cloneExerciseCollection(exerciseCollection);
  const newExercise = createWorkoutExercise({
    exercise: dragData.exercise,
    exOrder: 0,
    subOrder: 0,
    sectionId: dropZone.sectionId,
  });

  if (dropZone.type === "group-insert") {
    groups.splice(dropZone.index, 0, [newExercise]);
  } else {
    const targetGroup = groups[dropZone.groupIndex];
    if (!targetGroup) {
      return null;
    }
    targetGroup.splice(dropZone.position, 0, newExercise);
  }

  return {
    exerciseCollection: normalizeExerciseCollection(groups),
    recentDrop: {
      type: "exercise" as const,
      exerciseId: newExercise.sortableId,
    },
  };
};

export const getGroupDragId = (group: ExerciseCollection) =>
  `group-${group.map(({ sortableId }) => sortableId).join("-")}`;

export const getGroupKey = (group: ExerciseCollection) =>
  `group-key-${group.map(({ sortableId }) => sortableId).join("-")}`;

const remapSectionPositions = (sections: Workout["sections"]) =>
  sections.map((section, index) => ({
    ...section,
    position: index + 1,
    is_default: index === 0,
  }));

const concatGroupsInSectionOrder = (
  workout: Workout,
  sections: Workout["sections"],
) =>
  normalizeExerciseCollection(
    sections.flatMap((section) =>
      groupsForSection(workout, section.id).map(({ group }) => group),
    ),
  );

export const moveSectionToSectionIndex = (
  workout: Workout,
  dragData: SectionDragData,
  targetIndex: number,
) => {
  const from = workout.sections.findIndex(
    (section) => section.id === dragData.sectionId,
  );
  if (from < 0) {
    return null;
  }

  let to = Math.max(0, Math.min(targetIndex, workout.sections.length));
  if (from < to) {
    to -= 1;
  }
  if (to === from) {
    return null;
  }

  const sections = [...workout.sections];
  const [section] = sections.splice(from, 1);
  if (!section) {
    return null;
  }
  sections.splice(to, 0, section);
  const nextSections = remapSectionPositions(sections);

  return {
    workout: {
      ...workout,
      sections: nextSections,
      exerciseCollection: concatGroupsInSectionOrder(workout, nextSections),
    },
    recentDrop: {
      type: "section" as const,
      sectionId: dragData.sectionId,
    },
  };
};

const splitLooseSectionAt = (
  workout: Workout,
  sectionId: string,
  localIndex: number,
) => {
  const groups = groupsForSection(workout, sectionId);
  if (localIndex <= 0 || localIndex >= groups.length) {
    return { workout, newSectionId: null as string | null };
  }

  const newSection = createDefaultSection({
    is_default: false,
    position: 0,
  });
  const start = firstGroupIndexForSection(workout, sectionId);
  const exerciseCollection = cloneExerciseCollection(workout.exerciseCollection);
  for (let index = start + localIndex; index < start + groups.length; index += 1) {
    exerciseCollection[index] = stampGroupSection(
      exerciseCollection[index],
      newSection.id,
    );
  }

  const sectionIndex = workout.sections.findIndex(
    (section) => section.id === sectionId,
  );
  const sections = [...workout.sections];
  sections.splice(sectionIndex + 1, 0, newSection);

  return {
    workout: {
      ...workout,
      sections: remapSectionPositions(sections),
      exerciseCollection: normalizeExerciseCollection(exerciseCollection),
    },
    newSectionId: newSection.id,
  };
};

export const moveSectionToGroupIndex = (
  workout: Workout,
  dragData: SectionDragData,
  dropZone: GroupInsertDropZoneData,
) => {
  const targetId = dropZone.sectionId;
  if (!targetId) {
    return moveSectionToSectionIndex(
      workout,
      dragData,
      workout.sections.length,
    );
  }

  const target = workout.sections.find((section) => section.id === targetId);
  if (!target) {
    return null;
  }

  const groups = groupsForSection(workout, target.id);
  const start = firstGroupIndexForSection(workout, target.id);
  const localIndex = dropZone.index - start;
  const sectionIndex = workout.sections.findIndex(
    (section) => section.id === target.id,
  );

  if (!isLooseSection(target) || localIndex <= 0) {
    return moveSectionToSectionIndex(
      workout,
      dragData,
      localIndex <= 0 ? sectionIndex : sectionIndex + 1,
    );
  }

  if (localIndex >= groups.length) {
    return moveSectionToSectionIndex(workout, dragData, sectionIndex + 1);
  }

  const split = splitLooseSectionAt(workout, target.id, localIndex);
  if (!split.newSectionId) {
    return null;
  }

  const newHalfIndex = split.workout.sections.findIndex(
    (section) => section.id === split.newSectionId,
  );
  return moveSectionToSectionIndex(split.workout, dragData, newHalfIndex);
};

const toGroupInsertDropZone = (
  workout: Workout,
  dropZone: SectionAppendDropZoneData,
): GroupInsertDropZoneData => {
  const localIndex = groupsForSection(workout, dropZone.sectionId).length;
  return {
    type: "group-insert",
    index: insertIndexForSection(workout, dropZone.sectionId, localIndex),
    sectionId: dropZone.sectionId,
  };
};

export const applyWorkoutDrop = (
  workout: Workout,
  dragData: WorkoutDragData,
  dropZone: WorkoutDropZoneData,
) => {
  let nextWorkout = ensureWorkoutSections(workout);
  let nextDropZone = dropZone;

  if (
    "sectionId" in nextDropZone &&
    nextDropZone.sectionId === TRAILING_LOOSE_SECTION_ID
  ) {
    const trailing = ensureTrailingLooseSection(nextWorkout);
    nextWorkout = trailing.workout;
    nextDropZone = { ...nextDropZone, sectionId: trailing.sectionId };
  }

  if (nextDropZone.type === "section-append") {
    nextDropZone = toGroupInsertDropZone(nextWorkout, nextDropZone);
  }

  if (dragData.type === "section") {
    if (nextDropZone.type === "section-insert") {
      return moveSectionToSectionIndex(
        nextWorkout,
        dragData,
        nextDropZone.sectionIndex,
      );
    }
    if (nextDropZone.type === "group-insert") {
      return moveSectionToGroupIndex(nextWorkout, dragData, nextDropZone);
    }
    return null;
  }

  const result =
    dragData.type === "group"
      ? nextDropZone.type === "group-insert"
        ? moveGroupToIndex(
            nextWorkout.exerciseCollection,
            dragData,
            nextDropZone,
          )
        : null
      : dragData.type === "exercise"
        ? nextDropZone.type === "group-insert"
          ? moveExerciseToGroupIndex(
              nextWorkout.exerciseCollection,
              dragData,
              nextDropZone,
            )
          : nextDropZone.type === "superset-insert"
            ? moveExerciseToSupersetPosition(
                nextWorkout.exerciseCollection,
                dragData,
                nextDropZone,
              )
            : null
        : nextDropZone.type === "group-insert" ||
            nextDropZone.type === "superset-insert"
          ? insertLibraryExerciseAtDropZone(
              nextWorkout.exerciseCollection,
              dragData,
              nextDropZone,
            )
          : null;

  if (!result) {
    return null;
  }

  return {
    workout: {
      ...nextWorkout,
      exerciseCollection: result.exerciseCollection,
    },
    recentDrop: result.recentDrop,
  };
};

export const isWorkoutDragData = (value: unknown): value is WorkoutDragData =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  (value.type === "exercise" ||
    value.type === "group" ||
    value.type === "section" ||
    value.type === "library-exercise");

export const isWorkoutDropZoneData = (
  value: unknown,
): value is WorkoutDropZoneData =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  (value.type === "group-insert" ||
    value.type === "superset-insert" ||
    value.type === "section-insert" ||
    value.type === "section-append");

const isPointWithinRect = (
  point: { x: number; y: number },
  rect: { top: number; right: number; bottom: number; left: number },
) =>
  rect.top <= point.y &&
  point.y <= rect.bottom &&
  rect.left <= point.x &&
  point.x <= rect.right;

const getDistanceToRectCenter = (
  point: { x: number; y: number },
  rect: { top: number; left: number; width: number; height: number },
) => {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  return Math.hypot(point.x - centerX, point.y - centerY);
};

export const workoutCollisionDetection: CollisionDetection = (args) => {
  const { pointerCoordinates, droppableContainers, droppableRects } = args;

  if (pointerCoordinates) {
    const pointerCollisions = droppableContainers
      .map((droppableContainer) => {
        const rect = droppableRects.get(droppableContainer.id);

        if (!rect || !isPointWithinRect(pointerCoordinates, rect)) {
          return null;
        }

        return {
          id: droppableContainer.id,
          data: {
            droppableContainer,
            value: getDistanceToRectCenter(pointerCoordinates, rect),
          },
        };
      })
      .filter((collision) => collision !== null)
      .sort((a, b) => a.data.value - b.data.value);

    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
  }

  return rectIntersection(args);
};

type WorkoutDragOverlayContentProps = {
  activeDrag: WorkoutDragData | null;
};

export const WorkoutDragOverlayContent = ({
  activeDrag,
}: WorkoutDragOverlayContentProps) => {
  if (!activeDrag) {
    return null;
  }

  if (activeDrag.type === "section") {
    return (
      <Card className="w-[280px] border-foreground/10 bg-background/95 px-4 py-3 shadow-xl backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-foreground/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/80">
            Section
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {activeDrag.sectionName}
            </p>
            <p className="text-xs text-muted-foreground">
              {activeDrag.groupCount}{" "}
              {activeDrag.groupCount === 1 ? "exercise" : "exercises"}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (activeDrag.type === "group") {
    return (
      <Card className="w-[280px] border-foreground/10 bg-background/95 px-4 py-3 shadow-xl backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
            Superset
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">Superset</p>
            <p className="text-xs text-muted-foreground">
              {activeDrag.exerciseCount}{" "}
              {activeDrag.exerciseCount === 1 ? "exercise" : "exercises"}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const isLibraryExercise = activeDrag.type === "library-exercise";

  return (
    <Card className="w-[260px] border-foreground/10 bg-background/95 px-4 py-3 shadow-xl backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          {isLibraryExercise ? (
            <Plus className="h-4 w-4" />
          ) : (
            <Dumbbell className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {isLibraryExercise ? "New Exercise" : "Exercise"}
          </p>
          <p className="truncate text-sm font-medium">
            {activeDrag.exerciseName}
          </p>
        </div>
      </div>
    </Card>
  );
};
